import { InfoOutlined } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { notificationAtom } from "atoms/notification";
import { useAtom } from "jotai";
import { ReactElement, useEffect } from "react";
import { SnackbarColors } from "shared/constants";
import * as React from "react";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return (
      <MuiAlert
        sx={{ minWidth: "30ch" }}
        elevation={6}
        ref={ref}
        variant="filled"
        {...props}
      />
    );
  }
);

function Notification(): ReactElement {
  const [notification, setNotification] = useAtom(notificationAtom);
  const { isOpen, severity, message } = notification;

  useEffect(() => {
    // Auto-close only for success and info notifications
    if (isOpen && (severity === "success" || severity === "info")) {
      setTimeout(() => {
        setNotification({ isOpen: false, message: "", severity: "none" });
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setNotification((previousNotification) => ({
      ...previousNotification,
      isOpen: false,
    }));
  };

  const getColors = () => {
    let bgColor = "";
    let color = "white";
    if (severity === "error") bgColor = SnackbarColors.ErrorStatus;
    if (severity === "info") {
      bgColor = SnackbarColors.InfoStatus;
      color = "white";
    }
    if (severity === "warning") {
      bgColor = SnackbarColors.WarningStatus;
      color = "white";
    }
    if (severity === "success") bgColor = SnackbarColors.SuccessStatus;
    return { backgroundColor: bgColor, color };
  };

  // Get action component for close button (only for warning and error)
  const getAction = () => {
    if (severity === "warning" || severity === "error") {
      return (
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={handleClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      );
    }
    return null;
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      open={isOpen}
      message={severity === "none" && message}
      id={`notification-${severity}`}
      // Don't auto-hide warning/error alerts
      autoHideDuration={
        severity === "warning" || severity === "error" ? null : 2000
      }
      onClose={handleClose}
    >
      {severity !== "none" ? (
        <Alert
          sx={getColors()}
          id={`notification-alert-${severity}`}
          onClick={
            severity === "success" || severity === "info"
              ? handleClose
              : undefined
          }
          severity={severity}
          icon={false}
          action={getAction()}
        >
          <Stack direction="row" gap={1} alignItems="center">
            {severity === "success" ? (
              <CheckCircleOutlineIcon sx={{ color: "white" }} />
            ) : (
              <InfoOutlined sx={{ color: "white" }} />
            )}
            <Typography variant="body2">{message}</Typography>
          </Stack>
        </Alert>
      ) : (
        <span />
      )}
    </Snackbar>
  );
}

export default Notification;
